import * as numbers from "common/numbers";
import * as flagsResolver from "domain/flagsResolver";
import { createSelector } from "reselect";
import * as programSelectors from "store/selectors/input/program/programSelectors";
import * as stateSelectors from "store/selectors/stateSelectors";
import * as uuid from "uuid";

const getJudgmentsRaw = (state) => state.input?.flags?.judgments || [];
const getExposuresRaw = (state) => state.input?.flags?.exposures || [];

export const getJudgments = createSelector([getJudgmentsRaw], (judgments) => {
  return [
    ...judgments.map((judgment) => ({
      name: "",
      value: "",
      comments: "",
      canDelete: true,
      canEdit: true,
      ...judgment,
    })),
    {
      id: uuid.v4(),
      name: "",
      value: "",
      comments: "",
      canEdit: true,
      canDelete: false,
    },
  ];
});

export const getExposures = createSelector([getExposuresRaw], (exposures) => {
  return [
    ...exposures.map((exposure) => ({
      type: "",
      category: "",
      comments: "",
      canDelete: true,
      ...exposure,
    })),
  ];
});

// Primary GL Rating limit dropdowns (Common Limits) → numbers. These are the
// authoritative primary GL limits for GL Pricing, so an excess layer's
// structural limit never leaks into the GL primary rating.
const GL_LIMIT_FLAG_FIELD = {
  OCCURRENCE_LIMIT: "occurrenceLimit",
  GEN_AGG_LIMIT: "generalAggregate",
  PCO_AGG_LIMIT: "pcoAggregate",
  DAMAGE_TO_PREMISES: "damageToPremisesLimit",
  MED_PAY: "medicalExpenseLimit",
  PI_AI_LIMIT: "personalAndAdvertisingInjuryLimit",
  DEDUCTIBLE: "deductibleAmount",
  LIQUOR_LIABILITY_EACH_LIMIT: "liquorOccurrenceLimit",
  LIQUOR_LIABILITY_AGG_LIMIT: "liquorAggregateLimit",
};

export const selectPrimaryGlRatingLimits = createSelector(
  [getExposuresRaw],
  (exposures) => {
    const limits = {
      occurrenceLimit: null,
      generalAggregate: null,
      pcoAggregate: null,
      damageToPremisesLimit: null,
      medicalExpenseLimit: null,
      personalAndAdvertisingInjuryLimit: null,
      deductibleAmount: null,
      liquorOccurrenceLimit: null,
      liquorAggregateLimit: null,
    };
    for (const flag of exposures ?? []) {
      const field = GL_LIMIT_FLAG_FIELD[flag?.type];
      if (field) limits[field] = numbers.parseNumberString(flag.category);
    }
    return limits;
  }
);

const evaluateSinglePredicate = (key, expectedValue, context) => {
  const { program, flags } = context || {};
  const [type, field] = key.split(".");

  if (!type || !field) {
    return false;
  }

  let actualValue;
  switch (type) {
    case "program":
      actualValue = program?.[field];
      break;
    case "flag":
      if (Array.isArray(flags)) {
        const matchingFlag = flags.find((f) => f.flagKey === field);
        actualValue = matchingFlag?.category;
      } else {
        actualValue = flags?.[field];
      }
      break;
    default:
      return false;
  }

  if (Array.isArray(expectedValue)) {
    return expectedValue.includes(actualValue);
  }
  return actualValue === expectedValue;
};

const evaluateExposurePredicates = (exposure, context) => {
  const predicates = exposure.predicate;

  if (!predicates || typeof predicates !== "object") {
    return true;
  }

  if (predicates.or && typeof predicates.or === "object") {
    const orResult = Object.entries(
      predicates.or
    ).some(([key, expectedValue]) =>
      evaluateSinglePredicate(key, expectedValue, context)
    );

    const otherPredicates = Object.entries(predicates).filter(
      ([key]) => key !== "or"
    );

    if (otherPredicates.length > 0) {
      const regularResult = otherPredicates.every(([key, expectedValue]) =>
        evaluateSinglePredicate(key, expectedValue, context)
      );

      return orResult && regularResult;
    }

    return orResult;
  }

  return Object.entries(predicates).every(([key, expectedValue]) =>
    evaluateSinglePredicate(key, expectedValue, context)
  );
};

const evaluateIsTrueCondition = (exposure, context) => {
  const isTrueCondition = exposure.isTrue;

  if (!isTrueCondition || typeof isTrueCondition !== "object") {
    return exposure;
  }

  const shouldBeTrue = Object.entries(
    isTrueCondition
  ).some(([key, expectedValue]) =>
    evaluateSinglePredicate(key, expectedValue, context)
  );

  if (shouldBeTrue) {
    return {
      ...exposure,
      category: true,
    };
  }

  return exposure;
};

export const getExposuresForDisplay = createSelector(
  [programSelectors.selectResolvedProgram, getExposures],
  (program, exposures) => {
    const context = {
      program: program || {},
      flags: exposures,
    };

    const filteredExposures = exposures
      .filter((exposure) => evaluateExposurePredicates(exposure, context))
      .map((exposure) => evaluateIsTrueCondition(exposure, context));

    return [
      ...filteredExposures,
      {
        id: uuid.v4(),
        type: "",
        category: "",
        comments: "",
        canDelete: false,
      },
    ];
  }
);

const _filterNonEmptyJudgments = (judgments) =>
  judgments
    .filter(
      ({ value, name, comments }) =>
        (value != null && value !== "" && value !== "-") ||
        (name != null && name !== "") ||
        (comments != null && comments !== "")
    )
    .map(({ id, name, value, comments }) => ({
      id,
      name,
      value,
      comments,
    }));

const _filterNonEmptyValueJudgments = (judgments) =>
  judgments
    .filter(({ value }) => value != null && value !== "" && value !== "-")
    .map(({ id, name, value, comments }) => ({
      id,
      name,
      value,
      comments,
    }));

export const getNonEmptyValueJudgments = createSelector(
  [getJudgmentsRaw],
  _filterNonEmptyValueJudgments
);

export const getNonEmptyJudgments = createSelector(
  [getJudgmentsRaw],
  _filterNonEmptyJudgments
);

export const getJudgmentFactors = createSelector(
  [getNonEmptyValueJudgments],
  (judgments) => judgments.map((_) => _.value)
);

export const getJudgmentFactor = createSelector(
  [getJudgmentFactors],
  (judgments) =>
    judgments.map((_) => (100 + parseFloat(_)) / 100).reduce((a, b) => a * b, 1)
);

export const getJudgmentFactorPretty = createSelector(
  [getJudgmentFactor],
  (judgmentFactor) =>
    Math.round(((judgmentFactor || 0) - 1) * 100 * 10000) / 10000
);

export const selectUWJudgments = stateSelectors.createSelector(
  [(state) => state],
  (state) => _filterNonEmptyValueJudgments(state.input?.flags?.judgments ?? [])
);

export const selectStateId = (state) => state.input?.flags?.stateId ?? null;

export const selectFlags = (state) => state.input?.flags ?? null;

export const selectResolvedFlags = createSelector([selectFlags], (flags) =>
  flagsResolver.resolveFlagsInputs(flags)
);
